import { ComponentSpinner } from '@tebuto/layout/Spinner'
import TebutoModal from '@tebuto/modals/TebutoModal'
import MediumText from '@tebuto/typography/MediumText'
import { useCallback, useEffect, useRef, useState } from 'react'
import useBeforeUnload from './useBeforeUnload'
import useRouteChangeEvents from './useRouteChangeEvents'

export type TryLeaveAsyncResult = { kind: 'proceed' } | { kind: 'modal'; autoSaveErrorMessage?: string }

export default function useLeaveConfirmation(
    shouldPreventRouteChange: boolean,
    unsavedSections: string[],
    saveHandler: () => Promise<void>,
    discardHandler: () => void,
    tryLeaveAsync: () => Promise<TryLeaveAsyncResult>
) {
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
    const [isRedirecting, setIsRedirecting] = useState(false)
    const [onRouteChangeCompleteCallback, setOnRouteChangeCompleteCallback] = useState<() => void>()
    const [autoSaveErrorMessage, setAutoSaveErrorMessage] = useState<string | undefined>(undefined)
    const shouldPreventRouteChangeRef = useRef(shouldPreventRouteChange)

    useEffect(() => {
        shouldPreventRouteChangeRef.current = shouldPreventRouteChange
    }, [shouldPreventRouteChange])

    useBeforeUnload(shouldPreventRouteChange)

    const onBeforeRouteChange = useCallback(async () => {
        if (!shouldPreventRouteChange) {
            return true
        }

        const result = await tryLeaveAsync()
        if (result.kind === 'proceed') {
            return true
        }

        setAutoSaveErrorMessage(result.autoSaveErrorMessage)
        setShowConfirmationDialog(true)
        return false
    }, [shouldPreventRouteChange, tryLeaveAsync])

    const onRouteChangeComplete = useCallback(() => {
        setTimeout(() => {
            if (onRouteChangeCompleteCallback && isRedirecting) {
                onRouteChangeCompleteCallback()
                setOnRouteChangeCompleteCallback(undefined)
            }
            setShowConfirmationDialog(false)
            setAutoSaveErrorMessage(undefined)
            setIsRedirecting(false)
        }, 0)
    }, [isRedirecting, onRouteChangeCompleteCallback])

    const { allowRouteChange } = useRouteChangeEvents({ onBeforeRouteChange, onRouteChangeComplete })

    const allowRouterChangeWhenReady = useCallback(() => {
        setTimeout(() => {
            if (shouldPreventRouteChangeRef.current) {
                allowRouterChangeWhenReady()
            } else {
                allowRouteChange()
            }
        }, 50)
    }, [allowRouteChange])

    const handleManualRouteChange = useCallback(
        async (callback: () => void) => {
            const wrappedCallback = () => {
                callback()
            }
            setOnRouteChangeCompleteCallback(() => wrappedCallback)
            const shouldProceed = await onBeforeRouteChange()
            if (shouldProceed) {
                wrappedCallback()
            }
        },
        [onBeforeRouteChange]
    )

    const handleSave = useCallback(async () => {
        try {
            await saveHandler()
            setIsRedirecting(true)
            allowRouterChangeWhenReady()
        } catch (error) {
            console.error('Error during save:', error)
            setShowConfirmationDialog(false)
            setAutoSaveErrorMessage(undefined)
        }
    }, [saveHandler, allowRouterChangeWhenReady])

    const handleDiscard = useCallback(() => {
        discardHandler()
        setIsRedirecting(true)
        allowRouterChangeWhenReady()
    }, [discardHandler, allowRouterChangeWhenReady])

    const handleModalClose = useCallback(() => {
        setShowConfirmationDialog(false)
        setAutoSaveErrorMessage(undefined)
        setOnRouteChangeCompleteCallback(undefined)
    }, [])

    useEffect(() => {
        const abortController = new AbortController()
        window.addEventListener('routeChangeConfirmationEvent', onRouteChangeComplete, {
            signal: abortController.signal
        })
        return () => abortController.abort()
    }, [onRouteChangeComplete])

    return {
        handleManualRouteChange,
        confirmationDialog: (
            <TebutoModal
                title="Änderungen speichern"
                isOpen={showConfirmationDialog}
                onSubmit={handleSave}
                closeHandler={handleDiscard}
                onModalClose={handleModalClose}
                cancelButtonText="Verwerfen"
                className="max-w-125"
                verticalPadding={false}
            >
                {isRedirecting ? (
                    <Redirecting />
                ) : (
                    <SaveDecision unsavedSections={unsavedSections} autoSaveErrorMessage={autoSaveErrorMessage} />
                )}
            </TebutoModal>
        )
    }
}

function SaveDecision({
    unsavedSections,
    autoSaveErrorMessage
}: {
    unsavedSections: string[]
    autoSaveErrorMessage?: string
}) {
    return (
        <div className="grid gap-4 py-6">
            {autoSaveErrorMessage ? <MediumText className="text-destructive">{autoSaveErrorMessage}</MediumText> : null}
            <MediumText>Es gibt ungespeicherte Änderungen in diesen Sektionen:</MediumText>
            <ul className="list-disc pl-6">
                {unsavedSections.map((section) => (
                    <li key={section}>
                        <MediumText className="font-semibold">{section}</MediumText>
                    </li>
                ))}
            </ul>
        </div>
    )
}

function Redirecting() {
    return (
        <div className="py-10">
            <ComponentSpinner />
        </div>
    )
}
